import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import { Who } from '../shared/cells';
import { LabeledSegmented } from '../shared/controls';
import LoadFailed from '../shared/load_failed';
import { fetch_payout_holidays, post_payout_holiday, post_payout_holiday_remove } from '../api';
import {
  HOLIDAY_NAME_MAX,
  has_error,
  holiday_errors,
  holiday_years,
  holidays_view,
  payout_error
} from './adapters';
import { holiday_add_body, holiday_remove_body } from './requests';
import ListCard from './list_card';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { HOLIDAY_COLUMNS, payouts_csv_name } from './csv';

/*
 * the bank holidays that "2 bank working days" (when a teacher may report a payment as not
 * received) skips.
 */
const LOAD_FAILED_TEXT = 'Could not load the bank holidays.';
const SAVE_FAILED_TEXT = 'The holiday may not have been saved. Reload to check.';

const HolidaysPanel = ({ today, year: picked, on_year: setYear, on_notify, on_error }) => {
  const years = holiday_years(today);
  const year = years.indexOf(picked) !== -1 ? picked : years[0];
  const [state, setState] = useState({ status: 'loading', rows: [], error: '' });
  const [form, setForm] = useState({ date: '', name: '' });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState('');
  const [error, setError] = useState('');
  const seq = useRef(0);
  const errors = holiday_errors(form.date, form.name);

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setState((s) => ({ ...s, status: 'loading' }));
    fetch_payout_holidays(year)
      .then((d) => {
        if (mine === seq.current) setState({ status: 'ready', rows: holidays_view(d), error: '' });
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = payout_error(e, LOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, LOAD_FAILED_TEXT);
        setState({ status: 'error', rows: [], error: v.text });
      });
  }, [year, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  // Both writes answer the saved date's year: show it when it is the year on screen.
  const saved = (d, date, text) => {
    setBusy(false);
    setRemoving('');
    setError('');
    on_notify('success', text);
    if (date.slice(0, 4) === String(year))
      setState({ status: 'ready', rows: holidays_view(d), error: '' });
  };

  const failed = (e) => {
    setBusy(false);
    const v = payout_error(e, SAVE_FAILED_TEXT);
    if (v.kind === 'auth') on_error(e, SAVE_FAILED_TEXT);
    setError(v.text);
  };

  const add = (e) => {
    e.preventDefault();
    setTouched(true);
    if (busy || has_error(errors)) return;
    const body = holiday_add_body(form.date, form.name);
    setBusy(true);
    post_payout_holiday(body)
      .then((d) => {
        setForm({ date: '', name: '' });
        setTouched(false);
        saved(d, body.date, `Bank holiday saved: ${body.date} ${body.name}.`);
      })
      .catch(failed);
  };

  const remove = (date) => {
    if (busy) return;
    setBusy(true);
    post_payout_holiday_remove(holiday_remove_body(date))
      .then((d) => saved(d, date, `Bank holiday ${date} removed.`))
      .catch(failed);
  };

  const remove_cell = (h) =>
    removing === h.date ? (
      <span className="tp-c-acts tp-actions">
        <span>Remove {h.name}?</span>
        <button type="button" className="tp-link" onClick={() => remove(h.date)}>
          {busy ? 'Removing…' : 'Yes, remove'}
        </button>
        <button type="button" className="tp-link" onClick={() => setRemoving('')}>
          Keep
        </button>
      </span>
    ) : (
      <span className="tp-c-acts tp-actions">
        <button
          type="button"
          className="tp-link"
          aria-label={`Remove the ${h.date} holiday`}
          onClick={() => setRemoving(h.date)}
        >
          Remove
        </button>
      </span>
    );
  // Three tracks: date · name · remove.
  const columns = [
    {
      key: 'date',
      role: 'identifier',
      label: 'Date',
      track: { compact: '140px', full: '160px' },
      render: (h) => <Who name={h.date} />
    },
    { key: 'name', label: 'Name', render: (h) => <span className="tp-po-hol-name">{h.name}</span> },
    {
      key: 'action',
      role: 'actions',
      label: 'Action',
      track: { compact: '200px', full: '220px' },
      render: remove_cell
    }
  ];

  return (
    <div className="tp-po-holidays">
      <p className="tp-po-lead">
        Bank holidays are skipped when counting the 2 bank working days before a teacher can report
        a payment as not received. Weekends are skipped already.
      </p>
      <LabeledSegmented
        className="tp-po-years"
        label="Year"
        options={years.map((y) => ({ value: y, label: String(y) }))}
        value={year}
        onChange={setYear}
      />
      <form className="tp-filters tp-po-hol-form" onSubmit={add}>
        <div className="tp-field">
          <label htmlFor="tp-po-hol-date">Date</label>
          <input
            id="tp-po-hol-date"
            className="tp-input"
            type="date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
          />
        </div>
        <div className="tp-field tp-filter-wide">
          <label htmlFor="tp-po-hol-name">Name</label>
          <input
            id="tp-po-hol-name"
            className="tp-input"
            value={form.name}
            maxLength={HOLIDAY_NAME_MAX}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Diwali"
          />
        </div>
        <button
          type="submit"
          className="tp-btn tp-filter-apply"
          aria-disabled={busy ? 'true' : undefined}
        >
          Add holiday
        </button>
      </form>
      {touched && has_error(errors) ? (
        <div className="tp-neg tp-po-form-err" role="alert">
          {errors.date || errors.name}
        </div>
      ) : null}
      {error ? (
        <div className="tp-neg tp-po-form-err" role="alert">
          {error}
        </div>
      ) : null}
      <ListCard
        title={`Bank holidays · ${year}`}
        sub={state.status === 'ready' ? `${state.rows.length}` : ''}
        acts={
          state.status === 'ready' ? (
            <DownloadCsvButton
              columns={HOLIDAY_COLUMNS}
              rows={state.rows}
              file_name={payouts_csv_name('Bank holidays', String(year))}
            />
          ) : null
        }
      >
        {state.status === 'loading' ? <CustomLoading /> : null}
        {state.status === 'error' ? <LoadFailed text={state.error} on_retry={load} /> : null}
        {state.status === 'ready' ? (
          <DataTable
            columns={columns}
            rows={state.rows}
            rowKey={(h) => h.date}
            emptyText={`No bank holidays entered for ${year}.`}
            card_below={480}
          />
        ) : null}
      </ListCard>
    </div>
  );
};

export default HolidaysPanel;
